import { useTranslation } from 'react-i18next';
import type { ClimbScanColor } from '../../../types/climb-scan';
import type { ScanColorDraft } from './ScanClimbForm';

interface ScanColorCardProps {
  color: ClimbScanColor;
  draft: ScanColorDraft;
  selected: boolean;
  onSelect: () => void;
  /** How many marks are on the photo for this climb. */
  markCount: number;
}

/**
 * One of the colours a scan turned up, as a row in the list.
 *
 * A row and nothing more: the hold colour, the name, and how far along it is.
 * Picking it opens its form in the panel beside the photo rather than
 * underneath here — a form unfolding inside a list pushes everything below it
 * down the page, and puts the fields a long way from the marks they describe.
 */
export function ScanColorCard({ color, draft, selected, onSelect, markCount }: ScanColorCardProps) {
  const { t } = useTranslation();

  const swatches = color.hex_colors?.slice(0, 2) ?? [];
  const completed = color.status === 'completed';
  const title =
    draft.name || color.climb?.name || color.climb_name || color.color_name || t('gymAdmin.scans.unnamedColor');

  return (
    <button
      type="button"
      data-testid="scan-color-card"
      onClick={onSelect}
      aria-pressed={selected}
      className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted/50 ${
        selected ? 'border-primary bg-muted/50' : 'border-border'
      } ${completed ? 'bg-muted/40' : ''}`}
    >
      <span className="flex shrink-0 gap-0.5">
        {swatches.length > 0 ? (
          swatches.map((hex) => (
            <span key={hex} className="size-4 rounded-full border border-border" style={{ backgroundColor: hex }} />
          ))
        ) : (
          <span className="size-4 rounded-full border border-dashed border-border" />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{title}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {[
            color.color_name,
            color.hold_count ? t('gymAdmin.scans.holdCount', { count: color.hold_count }) : null,
            markCount > 0 ? t('gymAdmin.scans.markCount', { count: markCount }) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </span>

      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${
          completed
            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {t(`gymAdmin.scans.colorStatus.${color.status}`)}
      </span>
    </button>
  );
}
